import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, ChevronRight, AlertCircle, Plus, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useMatter } from '../hooks/useMatter';
import { checklistForVisa } from '../lib/documentChecklist';
import { ARCHIVED_STATUSES, isArchivedStatus } from '../lib/matterStatus';

interface MatterRow {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
  status: string;
  initial_payment_date: string | null;
  lodgement_date: string | null;
  updated_at: string;
  primary_contact: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  assigned_staff: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  reviewer_staff?: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
}

interface MattersProps {
  /** When 'archived', show only matters in a terminal status (Approved /
      Refused / Withdrawn). When 'active' (default), hide those terminal
      statuses so the working list isn't cluttered with closed matters. */
  mode?: 'active' | 'archived';
}

export default function Matters({ mode = 'active' }: MattersProps) {
  const { user } = useAuth();
  const { contact, isStaff, setActiveMatterId } = useMatter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<MatterRow[]>([]);

  const [search, setSearch] = useState('');
  // Initial status filter respects ?status=<canonical>. The special value
  // 'all-incl-archived' bypasses the active/archived split entirely so admins
  // can see every matter at every status in one screen.
  const initialStatus = searchParams.get('status') ?? 'all';
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [visaFilter, setVisaFilter] = useState<string>('all');
  const includeAllStatuses = statusFilter === 'all-incl-archived';
  // Per-matter approval %. Computed once per matters fetch using a single
  // documents query + the static visa checklist (same logic the per-matter
  // overview uses). Map: matter_id → percentComplete (0-100).
  const [percentByMatter, setPercentByMatter] = useState<Record<string, number>>({});
  // Sortable column state. Default: most-recent activity first.
  type SortKey = 'updated_at' | 'visa_subclass' | 'status' | 'percent' | 'client' | 'matter_id';
  const [sortKey, setSortKey] = useState<SortKey>('updated_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const initialFilterParam = searchParams.get('filter');
  // Admins viewing the archived list default to "All staff matters" (Eli's
  // requirement 2026-05-08 — he wants the full book in one glance, not a
  // "my matters only" view that hides his colleagues' approved/refused matters).
  // Non-admin staff still default to "My matters" in both views. Explicit
  // ?filter= in the URL always wins.
  const isAdminUser = contact?.staff_role === 'admin';
  const defaultAssigned: 'mine' | 'all' =
    initialFilterParam === 'all-staff' || initialFilterParam === 'all'
      ? 'all'
      : initialFilterParam === 'mine'
      ? 'mine'
      : isAdminUser && mode === 'archived'
      ? 'all'
      : 'mine';
  const [assignedFilter, setAssignedFilter] = useState<'mine' | 'all'>(defaultAssigned);

  // Keep the URL in sync so saved links and bookmarks reflect the chosen view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (assignedFilter === 'all') next.set('filter', 'all-staff');
    else next.delete('filter');
    if (statusFilter && statusFilter !== 'all') next.set('status', statusFilter);
    else next.delete('status');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [assignedFilter, statusFilter, searchParams, setSearchParams]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('matters')
          .select(
            `id, matter_id, visa_subclass, variant, status, initial_payment_date, lodgement_date, updated_at,
             primary_contact:contacts!matters_primary_contact_id_fkey (id, given_names, family_name, email),
             assigned_staff:contacts!matters_assigned_staff_id_fkey (id, given_names, family_name, email),
             reviewer_staff:contacts!matters_reviewer_staff_id_fkey (id, given_names, family_name, email)`,
          )
          .is('deleted_at', null)
          .order('updated_at', { ascending: false });
        if (err) throw err;
        if (!cancelled) {
          setRows((data ?? []) as unknown as MatterRow[]);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load matters');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Compute % complete per matter once the matters list lands. One bulk
  // documents query covers every matter; we then group locally by matter_id
  // and walk each matter's checklist to compute approved-vs-required.
  // Same weighting as the per-matter Documents header (approved=1.0,
  // uploaded-not-yet-approved=0.5) so the gamification is consistent.
  useEffect(() => {
    if (rows.length === 0) {
      setPercentByMatter({});
      return;
    }
    let cancelled = false;
    (async () => {
      const matterIds = rows.map((r) => r.id);
      const { data } = await supabase
        .from('documents')
        .select('matter_id, checklist_item, review_status, previous_version_id, id')
        .in('matter_id', matterIds)
        .is('deleted_at', null);
      if (cancelled) return;
      const docs = (data ?? []) as Array<{
        matter_id: string;
        checklist_item: string | null;
        review_status: string;
        previous_version_id: string | null;
        id: string;
      }>;
      // Per-matter: filter to active versions, then approved/uploaded sets.
      const byMatter = new Map<string, typeof docs>();
      for (const d of docs) {
        const list = byMatter.get(d.matter_id) ?? [];
        list.push(d);
        byMatter.set(d.matter_id, list);
      }
      const out: Record<string, number> = {};
      for (const r of rows) {
        const checklist = checklistForVisa(r.visa_subclass, r.variant);
        const required = checklist.filter((c) => c.required);
        if (required.length === 0) {
          out[r.id] = 0;
          continue;
        }
        const matterDocs = byMatter.get(r.id) ?? [];
        const supersededIds = new Set<string>();
        for (const d of matterDocs) {
          if (d.previous_version_id) supersededIds.add(d.previous_version_id);
        }
        const active = matterDocs.filter((d) => !supersededIds.has(d.id));
        const uploadedKeys = new Set(active.filter((d) => d.checklist_item).map((d) => d.checklist_item as string));
        const approvedKeys = new Set(active.filter((d) => d.review_status === 'approved').map((d) => d.checklist_item as string));
        const uploaded = required.filter((c) => uploadedKeys.has(c.key)).length;
        const approved = required.filter((c) => approvedKeys.has(c.key)).length;
        out[r.id] = Math.round(((approved + 0.5 * (uploaded - approved)) / required.length) * 100);
      }
      if (!cancelled) setPercentByMatter(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const setSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'updated_at' ? 'desc' : 'asc');
    }
  };

  const visaOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.visa_subclass));
    return Array.from(set).sort();
  }, [rows]);

  const statusOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.status));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const subset = rows.filter((r) => {
      // Active list hides terminal statuses; archived list shows ONLY terminal.
      // The special filter "all-incl-archived" bypasses both — admins use this
      // to scan the full book in one screen.
      const archived = isArchivedStatus(r.status);
      if (!includeAllStatuses) {
        if (mode === 'active' && archived) return false;
        if (mode === 'archived' && !archived) return false;
      }
      if (statusFilter !== 'all' && statusFilter !== 'all-incl-archived' && r.status !== statusFilter) return false;
      if (visaFilter !== 'all' && r.visa_subclass !== visaFilter) return false;
      // "Mine" matches when this user is the primary assignee OR the
      // secondary reviewer (e.g. Eli sees matters sent for his review).
      if (assignedFilter === 'mine' && contact) {
        const mine = r.assigned_staff?.id === contact.id || r.reviewer_staff?.id === contact.id;
        if (!mine) return false;
      }
      if (lower) {
        const fullName = `${r.primary_contact?.given_names ?? ''} ${r.primary_contact?.family_name ?? ''}`
          .toLowerCase()
          .trim();
        const email = r.primary_contact?.email?.toLowerCase() ?? '';
        const matterId = r.matter_id?.toLowerCase() ?? '';
        if (!fullName.includes(lower) && !email.includes(lower) && !matterId.includes(lower)) return false;
      }
      return true;
    });

    // Sort by chosen column (sortKey) + direction.
    const cmp = (a: MatterRow, b: MatterRow): number => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortKey) {
        case 'updated_at':
          av = a.updated_at; bv = b.updated_at; break;
        case 'visa_subclass':
          av = a.visa_subclass + (a.variant ?? ''); bv = b.visa_subclass + (b.variant ?? ''); break;
        case 'status':
          av = a.status; bv = b.status; break;
        case 'matter_id':
          av = a.matter_id ?? ''; bv = b.matter_id ?? ''; break;
        case 'percent':
          av = percentByMatter[a.id] ?? 0; bv = percentByMatter[b.id] ?? 0; break;
        case 'client': {
          av = `${a.primary_contact?.family_name ?? ''} ${a.primary_contact?.given_names ?? ''}`.trim().toLowerCase();
          bv = `${b.primary_contact?.family_name ?? ''} ${b.primary_contact?.given_names ?? ''}`.trim().toLowerCase();
          break;
        }
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    };
    return [...subset].sort(cmp);
  }, [rows, search, statusFilter, includeAllStatuses, visaFilter, assignedFilter, contact, sortKey, sortDir, percentByMatter, mode]);

  const handleOpenMatter = (id: string) => {
    setActiveMatterId(id);
    navigate(`/matters/${id}`);
  };

  if (!isStaff) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="mx-auto mb-3 text-slate-400" size={32} />
        <h2 className="text-lg font-semibold mb-1">Staff access only</h2>
        <p className="text-sm text-slate-600 mb-4">
          The matters list is for ADI staff. If you're a client, head back to your dashboard.
        </p>
        <Link to="/dashboard" className="btn-primary inline-flex">
          Go to dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#0B2341]">
            {includeAllStatuses
              ? 'All matters (every status)'
              : mode === 'archived'
              ? 'Approved matters'
              : 'Matters'}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {includeAllStatuses
              ? 'Active and archived matters merged into one view. Use the status dropdown to narrow down.'
              : mode === 'archived'
              ? 'Matters that are approved, refused, or withdrawn. These are kept for reference and reporting.'
              : 'All active client matters you have access to. Click a row to view the matter detail.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            {filtered.length} of {includeAllStatuses
              ? rows.length
              : mode === 'archived'
              ? rows.filter((r) => ARCHIVED_STATUSES.includes(r.status)).length
              : rows.filter((r) => !ARCHIVED_STATUSES.includes(r.status)).length} matters
          </span>
          {mode === 'active' && (
            <Link to="/matters/new" className="btn-primary">
              <Plus size={16} /> Create matter
            </Link>
          )}
        </div>
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-center">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search by client name, email, or matter ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-select min-w-[160px]"
          >
            <option value="all">All statuses</option>
            {isAdminUser && (
              <option value="all-incl-archived">All (incl. archived)</option>
            )}
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {humaniseStatus(s)}
              </option>
            ))}
          </select>
          <select
            value={visaFilter}
            onChange={(e) => setVisaFilter(e.target.value)}
            className="form-select min-w-[140px]"
          >
            <option value="all">All visas</option>
            {visaOptions.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value as 'mine' | 'all')}
            className="form-select min-w-[160px]"
          >
            <option value="mine">My matters</option>
            <option value="all">All staff matters</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading matters...</div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <EmptyState assignedFilter={assignedFilter} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <SortableTh active={sortKey === 'client'} dir={sortDir} onClick={() => setSort('client')}>Client</SortableTh>
                  <SortableTh active={sortKey === 'matter_id'} dir={sortDir} onClick={() => setSort('matter_id')}>Matter ID</SortableTh>
                  <SortableTh active={sortKey === 'visa_subclass'} dir={sortDir} onClick={() => setSort('visa_subclass')}>Visa</SortableTh>
                  <SortableTh active={sortKey === 'status'} dir={sortDir} onClick={() => setSort('status')}>Status</SortableTh>
                  <SortableTh active={sortKey === 'percent'} dir={sortDir} onClick={() => setSort('percent')}>% complete</SortableTh>
                  <th className="px-4 py-3">Case manager</th>
                  <SortableTh active={sortKey === 'updated_at'} dir={sortDir} onClick={() => setSort('updated_at')}>Last activity</SortableTh>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => handleOpenMatter(r.id)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {r.primary_contact?.id ? (
                        <Link
                          to={`/contacts/${r.primary_contact.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-[#0B2341] hover:text-[#2CABEA] hover:underline"
                        >
                          {r.primary_contact?.given_names ?? ''}{' '}
                          {r.primary_contact?.family_name ?? ''}
                          {!r.primary_contact?.given_names && !r.primary_contact?.family_name
                            ? '(no name)'
                            : ''}
                        </Link>
                      ) : (
                        <div className="font-semibold text-[#0B2341]">(no contact)</div>
                      )}
                      <div className="text-xs text-slate-500">
                        {r.primary_contact?.email ?? '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.matter_id ? (
                        <span className="font-mono text-xs text-slate-700 whitespace-nowrap">{r.matter_id}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.visa_subclass}</div>
                      {r.variant && (
                        <div className="text-xs text-slate-500 capitalize">{r.variant}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3">
                      <PercentCell percent={percentByMatter[r.id] ?? 0} />
                    </td>
                    <td className="px-4 py-3">
                      {r.assigned_staff ? (
                        <div className="text-slate-700">
                          {r.assigned_staff.given_names} {r.assigned_staff.family_name}
                        </div>
                      ) : (
                        <span className="text-slate-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatRelative(r.updated_at)}</td>
                    <td className="px-4 py-3 text-slate-400">
                      <ChevronRight size={16} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SortableTh({
  active,
  dir,
  onClick,
  children,
}: {
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <th className="px-4 py-3 select-none">
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-[#0B2341] transition-colors ${active ? 'text-[#0B2341]' : 'text-slate-500'}`}
      >
        {children}
        {active ? (
          dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
        ) : null}
      </button>
    </th>
  );
}

function PercentCell({ percent }: { percent: number }) {
  // Same colour ladder as Documents.tsx headline ring so the gamification reads consistently.
  const tone =
    percent >= 100 ? 'text-green-700 bg-green-50'
      : percent >= 75 ? 'text-emerald-700 bg-emerald-50'
      : percent >= 50 ? 'text-[#0B2341] bg-[#EBF7FD]'
      : percent >= 25 ? 'text-amber-700 bg-amber-50'
      : 'text-slate-500 bg-slate-50';
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded-md text-xs font-semibold tabular-nums ${tone}`}>
        {percent}%
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles = STATUS_STYLES[status] ?? { bg: 'bg-slate-100', text: 'text-slate-700' };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles.bg} ${styles.text}`}
    >
      {humaniseStatus(status)}
    </span>
  );
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700' },
  awaiting_review: { bg: 'bg-amber-50', text: 'text-amber-700' },
  ready_to_lodge: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  lodged: { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  granted: { bg: 'bg-green-100', text: 'text-green-800' },
  refused: { bg: 'bg-red-100', text: 'text-red-800' },
  on_hold: { bg: 'bg-slate-100', text: 'text-slate-700' },
  blocked: { bg: 'bg-red-50', text: 'text-red-700' },
};

function humaniseStatus(s: string): string {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} wk ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function EmptyState({ assignedFilter }: { assignedFilter: 'mine' | 'all' }) {
  return (
    <div className="p-12 text-center">
      <Filter className="mx-auto mb-3 text-slate-300" size={32} />
      <p className="font-medium text-slate-700 mb-1">No matters to show</p>
      <p className="text-sm text-slate-500">
        {assignedFilter === 'mine'
          ? "Try switching to 'All staff matters' or clearing the filters above."
          : 'Try clearing the filters above.'}
      </p>
    </div>
  );
}
